/**
 * ItemEditor - 3-Column Editor for Items
 *
 * Layout:
 * - Left Column: ItemFormFields (Details)
 * - Middle Column: Reserved for future use
 * - Right Column: Unified list of Attachments & Sub-items with drag-drop reordering
 *
 * Features:
 * - Draft persistence to localStorage (survives page refresh)
 * - Pending sub-items can be added before parent is saved
 * - Auto-save to draft on form changes
 * - Unified drag-drop list for attachments and sub-items
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ThreeColumnEditor } from '@/components/layout';
import { useItemStore, useAuthStore, useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  ItemFormFieldsCol1,
  ItemFormFieldsCol2,
  getDefaultFormValues,
  type ItemFormData,
} from './components/fields';
import { UnifiedChildList } from './components/UnifiedChildList';
import { AddChildButton } from './components/AddChildButton';
import {
  itemToFormData,
  formDataToRecord,
  recordToFormData,
  formDataToCreateItemInput,
  formDataToUpdateItemInput,
  formatEditorTimestamp,
} from './utils/formHelpers';
import {
  captureOriginalStateFromItem,
  captureOriginalStateFromDefaults,
  buildChildOrder,
} from './utils/unsavedChanges';
import { useUnsavedChanges } from './hooks';
import {
  saveItemDraft,
  loadItemDraft,
  loadItemDraftByItemId,
  clearItemDraft,
  createItemDraft,
  getCachedPendingFiles,
  addToCachedPendingFiles,
  removeFromCachedPendingFiles,
  clearCachedPendingFiles,
  setCachedPendingFiles,
  reorderPendingSubItems,
  type ItemDraft,
  type CachedPendingFile,
  type ChildOrderEntry,
} from '@/services';
import type {
  Item,
  NoteItem,
  ItemType,
  SubItem,
  Attachment,
} from '@perfin/shared';
import type { PendingSubItem } from '@/services';
import { getItemTypeLabel } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface ItemEditorProps {
  item?: Item;              // undefined = create mode
  initialType?: ItemType;   // for create mode
  draftId?: string;         // optional: resume specific draft
  draftVersion?: number;    // increment to trigger draft reload (after pending sub-item changes)
  onBack?: () => void;      // optional: smart back button
  onCancel: () => void;
  onSave: () => void;
  // Sub-item navigation handlers (inline editing, not modal)
  onEditSubItem?: (subItem: SubItem) => void;
  onAddSubItem?: (type: ItemType, parentFormData?: ItemFormData) => void;
  onEditPendingSubItem?: (pending: PendingSubItem) => void;
  // Callback when draft is created/loaded (for parent tracking)
  onDraftIdChange?: (draftId: string) => void;
  // Callback when a new item is created (for URL update)
  onItemCreated?: (itemId: string) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ItemEditor({
  item,
  initialType = 'task',
  draftId: initialDraftId,
  draftVersion,
  onBack,
  onCancel,
  onSave,
  onEditSubItem,
  onAddSubItem,
  onEditPendingSubItem,
  onDraftIdChange,
  onItemCreated,
}: ItemEditorProps) {
  const { user, profile } = useAuthStore();
  const { addItem, editItem, addSubItem, addItemAttachment, removeItemAttachment, reorderItemChildren, subItemsMap, loading, removeItem } = useItemStore();
  const { categories, members, entities, accounts } = useSettingsStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const isEditing = !!item;
  const currentType = item?.type || initialType;

  // Check if item is completed (task uses 'complete', others use 'final')
  const isItemCompleted = item ? (
    item.type === 'task'
      ? (item as { status?: string }).status === 'complete'
      : (item as { status?: string }).status === 'final'
  ) : false;

  // Draft state - use ref to avoid stale closures in callbacks
  const [draft, setDraft] = useState<ItemDraft | null>(null);
  const draftRef = useRef<ItemDraft | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<CachedPendingFile[]>([]);
  const [saving, setSaving] = useState(false);
  // Use undefined to indicate "not yet initialized", null for "initialized for new item"
  const initializedForItemId = useRef<string | null | undefined>(undefined);
  const [showFileDropZone, setShowFileDropZone] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [createdItemId, setCreatedItemId] = useState<string | null>(null);

  // Effective item ID: use existing ID or the one we just created
  const effectiveItemId = item?.id || createdItemId;

  // Load pending files from cache when draft is available
  useEffect(() => {
    if (draft?.draftId) {
      const cachedFiles = getCachedPendingFiles(draft.draftId);
      if (cachedFiles.length > 0) {
        setPendingAttachments(cachedFiles);
      }
    }
  }, [draft?.draftId]);

  // Keep draftRef in sync with draft state
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const form = useForm<ItemFormData>({
    defaultValues: item
      ? itemToFormData(item)
      : { ...getDefaultFormValues(currentType), owner: user?.uid || 'None' },
  });

  // Track form data that needs settings re-applied (use state to trigger effect)
  const [pendingSettingsData, setPendingSettingsData] = useState<Record<string, unknown> | null>(null);

  // Initialize or load draft - runs when item becomes available or changes
  useEffect(() => {
    // For editing: only initialize once per item
    // For creating: only initialize once (no itemId)
    const targetItemId = item?.id || null;

    // undefined = not initialized yet, null = initialized for new item, string = initialized for that item
    const alreadyInit = initializedForItemId.current !== undefined && initializedForItemId.current === targetItemId;

    if (alreadyInit) {
      return;
    }
    initializedForItemId.current = targetItemId;

    if (isEditing && item) {
      // Editing existing item - check for existing draft
      const existingDraft = loadItemDraftByItemId(item.id);

      if (existingDraft) {
        // Restore form data from draft (user's unsaved changes)
        const draftFormData = recordToFormData(existingDraft.formData, item.type);

        // Store the form data for settings re-apply (state change triggers effect)
        setPendingSettingsData(existingDraft.formData);

        // Ensure new fields exist for drafts created before unsaved changes feature
        if (!existingDraft.originalState) {
          const originalFormData = itemToFormData(item);
          existingDraft.originalState = captureOriginalStateFromItem(originalFormData, item);
        }
        existingDraft.removedAttachmentIds = existingDraft.removedAttachmentIds || [];
        existingDraft.removedSubItemIds = existingDraft.removedSubItemIds || [];

        // Update state and ref together
        draftRef.current = existingDraft;
        setDraft(existingDraft);
        saveItemDraft(existingDraft);
        onDraftIdChange?.(existingDraft.draftId);
        form.reset(draftFormData);
      } else {
        // Create new draft for editing
        const formData = itemToFormData(item);
        const newDraft = createItemDraft(item.type, item.id);
        newDraft.formData = formDataToRecord(formData);
        newDraft.originalState = captureOriginalStateFromItem(formData, item);
        newDraft.removedAttachmentIds = [];
        newDraft.removedSubItemIds = [];

        // Update state and ref together
        draftRef.current = newDraft;
        setDraft(newDraft);
        saveItemDraft(newDraft);
        onDraftIdChange?.(newDraft.draftId);
        form.reset(formData);
      }
    } else if (!isEditing) {
      // New item - check for existing draft or create new
      if (initialDraftId) {
        const existingDraft = loadItemDraft(initialDraftId);
        if (existingDraft) {
          const formData = recordToFormData(existingDraft.formData, existingDraft.currentType);
          if (!existingDraft.originalState) {
            existingDraft.originalState = captureOriginalStateFromDefaults(formData);
          }
          existingDraft.removedAttachmentIds = existingDraft.removedAttachmentIds || [];
          existingDraft.removedSubItemIds = existingDraft.removedSubItemIds || [];

          draftRef.current = existingDraft;
          setDraft(existingDraft);
          saveItemDraft(existingDraft);
          onDraftIdChange?.(existingDraft.draftId);
          form.reset(formData);
          return;
        }
      }
      // Create new draft
      const defaultFormValues = { ...getDefaultFormValues(currentType), owner: user?.uid || 'None' };
      const newDraft = createItemDraft(currentType);
      newDraft.originalState = captureOriginalStateFromDefaults(defaultFormValues);
      newDraft.removedAttachmentIds = [];
      newDraft.removedSubItemIds = [];

      draftRef.current = newDraft;
      setDraft(newDraft);
      saveItemDraft(newDraft);
      onDraftIdChange?.(newDraft.draftId);
      form.reset(defaultFormValues);
    }
  }, [item, isEditing, currentType, initialDraftId, form, onDraftIdChange]);

  // Reload draft when draftVersion changes (after pending sub-item added externally)
  useEffect(() => {
    if (draftVersion === undefined || draftVersion === 0) return;
    const currentDraft = draftRef.current;
    if (!currentDraft) return;

    // Reload draft from localStorage to get updated pendingSubItems
    const reloadedDraft = loadItemDraft(currentDraft.draftId);
    if (reloadedDraft) {
      draftRef.current = reloadedDraft;
      setDraft(reloadedDraft);
    }
  }, [draftVersion]);

  // Capture original child order once subItems are loaded (for reorder detection)
  const currentSubItems = item?.id ? (subItemsMap[item.id] || []) : [];
  const originalOrderCapturedForItemId = useRef<string | null>(null);

  useEffect(() => {
    // Reset when item changes
    if (item?.id !== originalOrderCapturedForItemId.current) {
      originalOrderCapturedForItemId.current = null;
    }

    // Only capture once per item, when editing
    if (!isEditing || !item || originalOrderCapturedForItemId.current === item.id) return;
    const currentDraft = draftRef.current;
    if (!currentDraft?.originalState) return;

    // Wait until subItems have loaded (length > 0 or we have attachments)
    const hasData = currentSubItems.length > 0 || (item.attachments?.length ?? 0) > 0;
    if (!hasData) return;

    // Only capture if not already set
    if (currentDraft.originalState.originalChildOrder) {
      originalOrderCapturedForItemId.current = item.id;
      return;
    }

    const childOrder = buildChildOrder(item.attachments || [], currentSubItems);
    const updatedOriginalState = {
      ...currentDraft.originalState,
      originalChildOrder: childOrder,
    };
    const updatedDraft = {
      ...currentDraft,
      originalState: updatedOriginalState,
      lastModified: Date.now(),
    };

    originalOrderCapturedForItemId.current = item.id;
    draftRef.current = updatedDraft;
    setDraft(updatedDraft);
    saveItemDraft(updatedDraft);
  }, [isEditing, item, currentSubItems]);

  // Auto-save to draft on form changes - uses ref to always have current draft
  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const currentDraft = draftRef.current;
      if (!currentDraft) return;

      const fv = formValues as ItemFormData;

      // Create updated draft with new form values
      const updatedDraft: ItemDraft = {
        ...currentDraft,
        formData: formDataToRecord(fv),
        lastModified: Date.now(),
      };

      // Update ref immediately for next change
      draftRef.current = updatedDraft;
      // Update state for React
      setDraft(updatedDraft);
      // Persist to localStorage
      saveItemDraft(updatedDraft);
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Re-apply dynamic dropdown values when settings load (after page refresh)
  // Categories, subCategories, members, entities, accounts are loaded async from Firestore
  // When form.reset runs, these options may not exist yet - the DOM <select> can't display
  // a value that isn't in its options, even though react-hook-form's internal state has it.
  useEffect(() => {
    // Only run if we have pending settings data
    if (!pendingSettingsData) {
      return;
    }

    const savedData = pendingSettingsData;
    const savedCategory = savedData.category as string;
    const savedSubCategory = savedData.subCategory as string;
    const savedOwner = savedData.owner as string;
    const savedEntityId = savedData.entityId as string;
    const savedAccountId = savedData.accountId as string;

    let allApplied = true;

    // Re-apply category when categories load
    if (savedCategory && categories.length > 0) {
      const categoryExists = categories.some(c => c.id === savedCategory) || savedCategory === 'None';
      if (categoryExists) {
        form.setValue('category', savedCategory, { shouldDirty: false, shouldTouch: false });

        // Also re-apply subCategory if we have one
        if (savedSubCategory) {
          const parentCategory = categories.find(c => c.id === savedCategory);
          const subCategoryExists = parentCategory?.subCategories?.some(s => s.id === savedSubCategory) || savedSubCategory === 'None';
          if (subCategoryExists) {
            form.setValue('subCategory', savedSubCategory, { shouldDirty: false, shouldTouch: false });
          }
        }
      }
    } else if (savedCategory) {
      allApplied = false; // Categories not loaded yet
    }

    // Re-apply owner when members load
    if (savedOwner && members.length > 0) {
      const ownerExists = members.some(m => m.userId === savedOwner) || savedOwner === 'None';
      if (ownerExists) {
        form.setValue('owner', savedOwner, { shouldDirty: false, shouldTouch: false });
      }
    } else if (savedOwner) {
      allApplied = false; // Members not loaded yet
    }

    // Re-apply entityId when entities load
    if (savedEntityId && entities.length > 0) {
      const entityExists = entities.some(e => e.id === savedEntityId) || savedEntityId === 'None';
      if (entityExists) {
        form.setValue('entityId', savedEntityId, { shouldDirty: false, shouldTouch: false });
      }
    } else if (savedEntityId) {
      allApplied = false; // Entities not loaded yet
    }

    // Re-apply accountId when accounts load
    if (savedAccountId && savedEntityId && accounts.length > 0) {
      const accountExists = accounts.some(a => a.id === savedAccountId && a.entityId === savedEntityId) || savedAccountId === 'None';
      if (accountExists) {
        form.setValue('accountId', savedAccountId, { shouldDirty: false, shouldTouch: false });
      }
    } else if (savedAccountId) {
      allApplied = false; // Accounts not loaded yet
    }

    // Clear pending data when all settings are applied
    if (allApplied) {
      setPendingSettingsData(null);
    }
  }, [categories, members, entities, accounts, form, pendingSettingsData]);

  const handleFileDrop = (files: File[]) => {
    const now = Date.now();
    const newEntries: CachedPendingFile[] = files.map((file, i) => ({
      file,
      addedAt: now + i, // Slight offset to preserve order within batch
    }));
    setPendingAttachments((prev) => [...prev, ...newEntries]);
    // Cache files so they survive navigation to sub-item editor
    if (draft?.draftId) {
      addToCachedPendingFiles(draft.draftId, files);
    }
    setShowFileDropZone(false); // Hide drop zone after files are dropped
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
    // Remove from cache too
    if (draft?.draftId) {
      removeFromCachedPendingFiles(draft.draftId, index);
    }
  };

  const handleAddAttachment = () => {
    setShowFileDropZone((prev) => !prev);
  };

  // Handle reordering of pending items
  const handleReorderPendingItems = useCallback((
    reorderedAttachments: CachedPendingFile[],
    reorderedSubItemIds: string[]
  ) => {
    // Update pending attachments in state and cache
    setPendingAttachments(reorderedAttachments);
    if (draft?.draftId) {
      setCachedPendingFiles(draft.draftId, reorderedAttachments);

      // Update pending sub-items order in draft
      reorderPendingSubItems(draft, reorderedSubItemIds);
      saveItemDraft(draft);
      // Trigger re-render by updating draft state
      setDraft({ ...draft });
    }
  }, [draft]);

  // Defer attachment removal - track IDs to delete on save
  const handleRemoveExistingAttachment = useCallback((attachment: Attachment) => {
    setDraft((currentDraft) => {
      if (!currentDraft) return null;
      // Add to removed list if not already there
      if (currentDraft.removedAttachmentIds.includes(attachment.id)) {
        return currentDraft;
      }
      const updatedDraft = {
        ...currentDraft,
        removedAttachmentIds: [...currentDraft.removedAttachmentIds, attachment.id],
        lastModified: Date.now(),
      };
      saveItemDraft(updatedDraft);
      return updatedDraft;
    });
  }, []);

  // Defer reorder of saved items - track new order to persist on save
  const handleReorderSavedItems = useCallback((reorderedChildren: ChildOrderEntry[]) => {
    setDraft((currentDraft) => {
      if (!currentDraft) return null;
      const updatedDraft = {
        ...currentDraft,
        reorderedChildren,
        lastModified: Date.now(),
      };
      saveItemDraft(updatedDraft);
      return updatedDraft;
    });
  }, []);

  // Filter visible attachments (exclude ones marked for removal)
  const visibleAttachments = useMemo(() => {
    if (!item?.attachments) return [];
    const removedIds = draft?.removedAttachmentIds || [];
    return item.attachments.filter((a) => !removedIds.includes(a.id));
  }, [item?.attachments, draft?.removedAttachmentIds]);

  // Pending sub-item handlers
  const handleRemovePendingSubItem = useCallback((tempId: string) => {
    setDraft((currentDraft) => {
      if (!currentDraft) return null;
      const updatedDraft = {
        ...currentDraft,
        pendingSubItems: currentDraft.pendingSubItems.filter((s) => s.tempId !== tempId),
        lastModified: Date.now(),
      };
      saveItemDraft(updatedDraft);
      return updatedDraft;
    });
  }, []);

  // Core save logic - returns true on success
  const performSave = async (data: ItemFormData): Promise<boolean> => {
    if (!user) return false;

    try {
      let itemId = effectiveItemId;

      // Get organizationId from user profile (for org context)
      const organizationId = profile?.organizationId;

      if (effectiveItemId) {
        const updateInput = formDataToUpdateItemInput(data, currentType, organizationId);
        await editItem(item?.createdBy || user.uid, effectiveItemId, updateInput);
        toast.success(`${getItemTypeLabel(currentType)} updated`);
      } else {
        const createInput = formDataToCreateItemInput(data, currentType, organizationId);
        itemId = await addItem(user.uid, createInput);
        setCreatedItemId(itemId);
        // Update draft's itemId so loadItemDraftByItemId can find it on remount
        if (draftRef.current) {
          draftRef.current.itemId = itemId;
          saveItemDraft(draftRef.current);
        }
        onItemCreated?.(itemId);
        toast.success(`${getItemTypeLabel(currentType)} created`);
      }

      // Delete removed attachments (deferred deletion)
      if (isEditing && item && draft && draft.removedAttachmentIds.length > 0) {
        for (const attachmentId of draft.removedAttachmentIds) {
          const attachment = item.attachments?.find((a) => a.id === attachmentId);
          if (attachment) {
            try {
              await removeItemAttachment(item.createdBy, item.id, attachment);
            } catch (err) {
              console.error('Failed to remove attachment:', err);
            }
          }
        }
      }

      // Persist deferred reorder (if any)
      if (isEditing && item && draft && draft.reorderedChildren && draft.reorderedChildren.length > 0) {
        try {
          await reorderItemChildren(item.createdBy, item.id, draft.reorderedChildren);
        } catch (err) {
          console.error('Failed to reorder items:', err);
        }
      }

      // Save pending attachments and sub-items in their intended order (by addedAt timestamp)
      if (itemId && organizationId) {
        // Build unified list of pending items with timestamps
        type PendingItem =
          | { kind: 'attachment'; addedAt: number; file: File }
          | { kind: 'subItem'; addedAt: number; input: PendingSubItem['input'] };
        const pendingItems: PendingItem[] = [];

        // Add pending attachments
        for (const cached of pendingAttachments) {
          pendingItems.push({ kind: 'attachment', addedAt: cached.addedAt, file: cached.file });
        }

        // Add pending sub-items (extract timestamp from tempId: temp_<timestamp>_<random>)
        if (draft?.pendingSubItems) {
          for (const pending of draft.pendingSubItems) {
            const timestampMatch = pending.tempId.match(/^temp_(\d+)_/);
            const addedAt = timestampMatch ? parseInt(timestampMatch[1], 10) : Date.now();
            pendingItems.push({ kind: 'subItem', addedAt, input: pending.input });
          }
        }

        // Sort by addedAt to preserve intended order
        pendingItems.sort((a, b) => a.addedAt - b.addedAt);

        // Use item's createdBy for existing items, user.uid for new items
        const creatorId = isEditing && item ? item.createdBy : user.uid;

        // Save in order
        for (const pending of pendingItems) {
          if (pending.kind === 'attachment') {
            await addItemAttachment(creatorId, itemId, pending.file, organizationId);
          } else {
            try {
              // Deserialize dates from localStorage (JSON.parse leaves them as strings)
              const input = {
                ...pending.input,
                dueDate: pending.input.dueDate ? new Date(pending.input.dueDate as unknown as string) : undefined,
              };
              await addSubItem(creatorId, itemId, input);
            } catch (err) {
              console.error('Failed to create sub-item:', err);
              toast.error(`Failed to create sub-item: ${pending.input.name}`);
            }
          }
        }
      }

      // Clear pending files (they've been uploaded)
      if (draft) {
        clearCachedPendingFiles(draft.draftId);
      }
      setPendingAttachments([]);

      // Return success - caller decides whether to clear or reset draft
      return true;
    } catch (error) {
      console.error('Item save error:', error);
      toast.error(
        isEditing
          ? `Failed to update ${getItemTypeLabel(currentType).toLowerCase()}`
          : `Failed to create ${getItemTypeLabel(currentType).toLowerCase()}`
      );
      return false;
    }
  };

  // Save only (stay on page) - only for edit mode
  const handleSave = form.handleSubmit(async (data) => {
    setSaving(true);
    const success = await performSave(data);
    setSaving(false);

    // Reset draft with new original state (form data is now "saved" state)
    if (success && draft) {
      const newOriginalState = captureOriginalStateFromDefaults(data);
      // For edit mode, also capture the current child order as original
      if (item) {
        const currentSubItems = subItemsMap[item.id] || [];
        newOriginalState.originalChildOrder = buildChildOrder(
          item.attachments || [],
          currentSubItems
        );
      }
      const resetDraft: ItemDraft = {
        ...draft,
        originalState: newOriginalState,
        removedAttachmentIds: [],
        removedSubItemIds: [],
        pendingSubItems: [],
        reorderedChildren: undefined,
        lastModified: Date.now(),
      };
      draftRef.current = resetDraft;
      setDraft(resetDraft);
      saveItemDraft(resetDraft);
      setHasChanges(false);
    }
  });

  // Save and close (navigate away)
  const handleSaveAndClose = form.handleSubmit(async (data) => {
    setSaving(true);
    const success = await performSave(data);
    setSaving(false);
    if (success) {
      // Clear draft completely when closing
      if (draft) {
        clearItemDraft(draft.draftId);
      }
      onSave();
    }
  });

  // Unsaved changes detection
  const { confirmIfUnsaved, checkChanges } = useUnsavedChanges({
    draft,
    getFormData: form.getValues,
    pendingAttachmentCount: pendingAttachments.length,
    enabled: !saving,
    draftType: 'item',
  });

  // Track changes for Save button state (both create and edit modes)
  useEffect(() => {
    const result = checkChanges();
    setHasChanges(result.hasChanges);
  }, [checkChanges, draft, pendingAttachments.length]);

  // Also update hasChanges when form values change
  useEffect(() => {
    const subscription = form.watch(() => {
      const result = checkChanges();
      setHasChanges(result.hasChanges);
    });
    return () => subscription.unsubscribe();
  }, [form, checkChanges]);

  // Back/cancel with unsaved changes check
  const handleBack = useCallback(() => {
    const navigateBack = onBack || onCancel;
    if (!draft) {
      navigateBack();
      return;
    }

    confirmIfUnsaved(
      () => {
        clearCachedPendingFiles(draft.draftId);
        clearItemDraft(draft.draftId);
        navigateBack();
      },
      {
        title: 'Unsaved Changes',
        onSave: handleSaveAndClose,
      }
    );
  }, [draft, confirmIfUnsaved, onBack, onCancel, handleSaveAndClose]);

  const handleCancel = useCallback(() => {
    if (!draft) {
      onCancel();
      return;
    }

    confirmIfUnsaved(
      () => {
        clearCachedPendingFiles(draft.draftId);
        clearItemDraft(draft.draftId);
        onCancel();
      },
      {
        title: 'Unsaved Changes',
        onSave: handleSaveAndClose,
      }
    );
  }, [draft, confirmIfUnsaved, onCancel, handleSaveAndClose]);

  // Handle delete button click
  const handleDelete = useCallback(() => {
    if (!item) return;

    confirmDelete({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item.name}" and all its notifications? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeItem(item.createdBy, item.id);
          toast.success(`${getItemTypeLabel(currentType)} deleted`);
          if (draft) {
            clearCachedPendingFiles(draft.draftId);
            clearItemDraft(draft.draftId);
          }
          onCancel();
        } catch {
          toast.error(`Failed to delete ${getItemTypeLabel(currentType).toLowerCase()}`);
        }
      },
    });
  }, [item, confirmDelete, removeItem, toast, currentType, draft, onCancel]);

  const title = isEditing
    ? `Edit ${getItemTypeLabel(currentType)}`
    : `New ${getItemTypeLabel(currentType)}`;

  // Header metadata: Created and Completed/Finalized timestamps
  const headerMetadata = useMemo(() => {
    if (!item) return null;

    const anyItem = item as unknown as Record<string, unknown>;
    const createdAt = formatEditorTimestamp(anyItem.createdAt as { toDate: () => Date } | undefined);
    const completedAt = item.type === 'task'
      ? formatEditorTimestamp(anyItem.completedAt as { toDate: () => Date } | undefined)
      : formatEditorTimestamp(anyItem.finalizedAt as { toDate: () => Date } | undefined);
    const completionLabel = item.type === 'task' ? 'Completed' : 'Finalized';

    if (!createdAt && !completedAt) return null;

    return (
      <span className="text-sm text-gray-500 ml-4">
        {createdAt && <span>Created: {createdAt}</span>}
        {createdAt && completedAt && <span className="mx-2">|</span>}
        {completedAt && <span>{completionLabel}: {completedAt}</span>}
      </span>
    );
  }, [item]);

  // Column 2 title varies by type
  const getCol2Title = () => {
    if (currentType === 'transaction') return 'Details and Dates';
    return 'Dates';
  };

  return (
    <div className="mb-[60px]">
      <ThreeColumnEditor
        title={title}
        headerMetadata={headerMetadata}
        onBack={handleBack}
        onCancel={handleCancel}
        onSave={handleSave}
        onSaveAndClose={handleSaveAndClose}
        saving={saving || loading}
        saveDisabled={!hasChanges}
        onDelete={isEditing ? handleDelete : undefined}
        deleteDisabled={saving || loading}
        leftTitle="Details"
        middleTitle={getCol2Title()}
        rightTitle="Attachments & Sub-items"
        rightHeaderAction={
          !isItemCompleted && (
            <AddChildButton
              onAddAttachment={handleAddAttachment}
              onAddSubItem={onAddSubItem ? (type: ItemType) => onAddSubItem(type, form.getValues()) : undefined}
              allowedTypes={['task', 'keyDate', 'transaction', 'note']}
              disabled={saving || !draft}
            />
          )
        }
        leftPanel={
          <ItemFormFieldsCol1
            type={currentType}
            form={form}
            disabled={saving}
            statusOnlyEditable={isItemCompleted}
            subItems={currentType === 'task' ? currentSubItems : undefined}
          />
        }
        middlePanel={
          <ItemFormFieldsCol2
            type={currentType}
            form={form}
            disabled={saving}
            statusOnlyEditable={isItemCompleted}
            fullWebPageHTML={item?.type === 'note' ? (item as NoteItem).fullWebPageHTML : undefined}
            sourceUrl={item?.type === 'note' && (item as NoteItem).urls?.length ? (item as NoteItem).urls![0] : undefined}
            createdAtDisplay={item?.createdAt ? formatEditorTimestamp(item.createdAt) || undefined : undefined}
            aiSummary={item?.type === 'note' ? (item as NoteItem).aiSummary : undefined}
            itemId={item?.id}
            itemCreatedBy={item?.createdBy}
          />
        }
        rightPanel={
          item ? (
            // Editing existing item - unified list
            <UnifiedChildList
              itemId={item.id}
              itemCreatedBy={item.createdBy}
              attachments={visibleAttachments}
              pendingAttachments={pendingAttachments}
              pendingSubItems={draft?.pendingSubItems || []}
              reorderedChildren={draft?.reorderedChildren}
              showFileDropZone={showFileDropZone}
              onFilesDrop={handleFileDrop}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onRemovePendingAttachment={handleRemovePendingFile}
              onRemovePendingSubItem={handleRemovePendingSubItem}
              onReorderPendingItems={handleReorderPendingItems}
              onReorderSavedItems={handleReorderSavedItems}
              onEditSubItem={onEditSubItem}
              onEditPendingSubItem={onEditPendingSubItem}
              disabled={saving || isItemCompleted}
            />
          ) : (
            // New item - unified list with pending items only
            <UnifiedChildList
              itemId=""
              attachments={[]}
              pendingAttachments={pendingAttachments}
              pendingSubItems={draft?.pendingSubItems || []}
              showFileDropZone={showFileDropZone}
              onFilesDrop={handleFileDrop}
              onRemoveExistingAttachment={() => {}}
              onRemovePendingAttachment={handleRemovePendingFile}
              onRemovePendingSubItem={handleRemovePendingSubItem}
              onReorderPendingItems={handleReorderPendingItems}
              onEditPendingSubItem={onEditPendingSubItem}
              disabled={saving}
            />
          )
        }
      />
    </div>
  );
}
